<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData :coaching="coachingData"/>
        <Menu/>

        <div class="row text-right">
          <div class="col-12">
            <button type="button" class="btn btn-primary" @click="handleProtocolModalForm">Protokoll hinzufügen
            </button>
            <button type="button" class="btn btn-outline-primary" @click="handleNoteModalForm">Zwischennotiz
            </button>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-5">
          <table class="table table-striped table-hover">
            <thead>
            <tr>
              <th style="font-size: 12px;">Termin</th>
              <th style="font-size: 12px;">Anwesenheit</th>
              <th style="font-size: 12px;">Sitzungsthema</th>
              <th class="text-right" style="font-size: 12px;">Einheiten</th>
              <th style="font-size: 12px;">Modul</th>
              <th style="font-size: 12px;">Coachart</th>
              <th style="font-size: 12px;">Coach</th>
              <th style="font-size: 12px;">Beschreibung</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">
                {{ $t('general.actions') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="protocol in protocolList"
                :key="protocol.id"
                :class="['table-row', { 'bg-light-gray': protocol.note }]"
                @click="handleClickOnEdit(protocol)">

              <template v-if="!protocol.isNote">
                <th scope="row">{{ protocol?.termin }}</th>
                <td>{{ protocol?.presenceTranslation }}</td>
                <td>{{ truncate(protocol?.meetingTopic, 30) }}</td>
                <td class="text-right">{{ protocol?.units }}</td>
                <td>{{protocol.module.name}}</td>
                <td>{{protocol.coachTypeTranslation}}</td>
                <td>{{ protocol?.coach?.fullName }}</td>
                <td>{{ truncate(protocol?.description, 60) }}</td>
              </template>
              <template v-else>
                <th scope="row">{{ protocol?.termin }}</th>
                <td colspan="7">
                  {{ protocol?.createdByUserData?.fullName }}
                  <span style="margin: 0 15px;">|</span>
                  {{ truncate(protocol?.description, 180) }}
                </td>
              </template>

              <td class="text-center">
                <div v-if="!protocol.invoiced" style="float: right; width: 90px;">
                  <button class="btn btn-danger ml-2" @click.stop="handleDeleteModal(protocol?.id)">
                    <i class="os-icon os-icon-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Modal
          v-model="showProtocolModalForm"
          title="Protokoll"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          :customWidth="'1200px'"
          :hide-footer="true"
          classFooter="justify-content-center">
        <Form :protocolId="protocolId"
              :coachingId="coachingId"
              :operationType="operationType"
              v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showNoteModalForm"
          title="Zwischennotiz"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-xl"
          :hide-footer="true"
          classFooter="justify-content-center">
        <NoteForm :protocolId="protocolId"
                  :coachingId="coachingId"
                  v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteProtocol">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>

import portalPrivateLearningCoachingService from '@/api/Portal/PrivateLearningCoaching';
import unemployedPrivateLearningCoachingService from '@/api/Unemployed/PrivateLearningCoaching';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import Menu from '@/views/App/Portal/PrivateLearningCoaching/Menu.vue';
import CoachingOverviewData from '@/views/App/Portal/PrivateLearningCoaching/CoachingOverviewData.vue';
import Form from '@/views/App/Portal/PrivateLearningCoaching/Protocol/Form.vue';
import NoteForm from '@/views/App/Portal/PrivateLearningCoaching/Protocol/NoteForm.vue';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t }                 = useI18n();
const route                 = useRoute();
const coachingData          = ref('');
const operationType         = ref('');
const protocolId            = ref('');
const protocolList          = ref([]);
const coachingId            = +route.params.id;
const isLoaded              = ref(false);
const isDigitalSignature    = ref(false);
const showProtocolModalForm = ref(false);
const showNoteModalForm     = ref(false);
const showDeleteModal       = ref(false);
const submitForm            = ref(false);

watch(submitForm, () => {
  protocolId.value            = null;
  showProtocolModalForm.value = false;
  showNoteModalForm.value     = false;
  showDeleteModal.value       = false;
  getCoachingData();
  getProtocolList();
});

async function getProtocolList() {
  try {
    const { data }     = await portalPrivateLearningCoachingService.getProtocolList(coachingId);
    protocolList.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getCoachingData() {
  try {
    CloseToast();
    const { data } = await unemployedPrivateLearningCoachingService.getPrivateLearningCoaching(+coachingId);

    coachingData.value       = data.data;
    operationType.value      = data?.data?.nameOfCoaching?.operationType;
    isDigitalSignature.value = data.data.digitalSignature;
  } catch (error) {
    await ErrorToast(error);
  }
}

function handleClickOnEdit(protocol) {
  CloseToast();
  if (protocol.isNote) {
    handleEditNoteModalForm(protocol.id);
  } else {
    handleEditProtocolModalForm(protocol.id);
  }
}

function handleProtocolModalForm() {
  CloseToast();
  protocolId.value            = '';
  showProtocolModalForm.value = true;
  showNoteModalForm.value     = false;
  showDeleteModal.value       = false;
}

function handleEditProtocolModalForm(id) {
  CloseToast();
  protocolId.value            = id;
  showProtocolModalForm.value = true;
  showNoteModalForm.value     = false;
  showDeleteModal.value       = false;
}

function handleNoteModalForm() {
  CloseToast();
  protocolId.value            = '';
  showNoteModalForm.value     = true;
  showProtocolModalForm.value = false;
  showDeleteModal.value       = false;
}

function handleEditNoteModalForm(id) {
  CloseToast();
  protocolId.value            = id;
  showNoteModalForm.value     = true;
  showProtocolModalForm.value = false;
  showDeleteModal.value       = false;
}

function handleDeleteModal(id) {
  CloseToast();
  protocolId.value            = id;
  showDeleteModal.value       = true;
  showProtocolModalForm.value = false;
  showNoteModalForm.value     = false;
}

const truncate = (text, length) => {
  if (!text || typeof text !== 'string') {
    return ''; // Return an empty string if text is null, undefined, or not a string
  }

  const strippedText = text.replace(/<\/?[^>]+(>|$)/g, ''); // Remove HTML tags
  return strippedText.length > length ? strippedText.substring(0, length) + '...' : strippedText;
};

async function deleteProtocol() {
  try {
    await portalPrivateLearningCoachingService.protocolDelete(protocolId.value);

    protocolId.value      = null;
    showDeleteModal.value = false;

    await getCoachingData();
    await getProtocolList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getCoachingData(),
    getProtocolList(),
  ]);
  isLoaded.value = true;
});
</script>

<style scoped>

</style>